import { useMutation } from '@tanstack/react-query';
import { QuoteDTO } from '@setvi/shared/services/react-query';
import { updateQuoteStatusMutation } from 'Services';
import { useQuoteCache } from './use-quote-cache';

interface UpdateQuoteStatusVariables {
  statusId: string;
}

/**
 * Update quote status with optimistic updates
 * Only updates statusId, does not trigger full quote edit mutation
 */
export const useUpdateQuoteStatus = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);

  return useMutation({
    ...updateQuoteStatusMutation(),
    mutationFn: ({ statusId }: UpdateQuoteStatusVariables) =>
      updateQuoteStatusMutation().mutationFn({ quoteId, statusId }),

    onMutate: async ({ statusId }: UpdateQuoteStatusVariables) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();

      cache.setQuote((old: QuoteDTO | undefined) => {
        if (!old) return old;
        return { ...old, statusId };
      });

      return { previousQuote };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
    },

    onSuccess: () => {
      cache.invalidate();
    }
  });
};
